<template>
  <div class="ss-salon-dashboard-view">
    <!-- Header -->
    <div class="ss-dashboard-header">
      <div
        class="ss-dashboard-header__content"
        :class="{
          'ss-dashboard-header__content--with-switcher': $_ssGN_showSwitcher
        }"
      >
        <div
          class="ss-dashboard-header__graphic"
          @click="$router.push({
            path: `${$route.path.replace(/\/$/, '')}/account/profile`,
            params: {
              salonId: salonId
            }
          }).catch(() => {})"
        >
          <oct-media
            :image="me.profileIcon"
            :alt="`${me.lastName} ${me.firstName}`"
            class="ss-dashboard-header__image"
          />
        </div>

        <!-- Title -->
        <div class="ss-dashboard-header__title">
          <span class="ss-dashboard-header__title-text">
            {{ $_octPrototypeView_getDateString(new Date) }}
          </span>
        </div>

        <!-- Switcher -->
        <div
          class="ss-dashboard-header__switcher"
          v-if="$_ssGN_showSwitcher"
        >
          <!-- Switch -->
          <div class="ss-dashboard-header__switch">
            <oct-switch 
              :texts="['ダッシュボード', '施術管理']"
              v-model="$data.$_ssGN_switchValue"
            />
          </div>
        </div>

        <!-- Actions -->
        <div
          class="ss-dashboard-header__actions"
          v-if="me.data.verified !== false"
        >
          <!-- Comments -->
          <div
            class="ss-dashboard-header__action"
            @click="$router.push({
              path: `${$route.path.replace(/\/$/, '')}/account/comments`,
              params: {
                salonId: salonId
              }
            })"
          >
            <oct-icon icon="message-outline" />
          </div>

          <!-- Notifications -->
          <div
            class="ss-dashboard-header__action"
            :class="{
              'ss-dashboard-header__action--activated': !isNotificationRead
            }"
            @click="$router.push({
              path: `${$route.path.replace(/\/$/, '')}/account/notifications`,
              params: {
                salonId: salonId
              }
            })"
          >
            <oct-icon icon="bel-outline" v-if="isNotificationRead" />
            <oct-icon icon="bel" v-else />
          </div>

          <div class="ss-dashboard-header__action ss-dashboard-header__action--exit">
            <oct-icon icon="exit" @click.native="signOut" />
          </div>
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="ss-salon-dashboard-view__tabs">
      <oct-tabs
        :items="$data.$_ssSDV_tabs"
        @click:item="onClickTab"
      />
    </div>

    <!-- Content -->
    <div class="ss-salon-dashboard-view__content">
      <!-- Header -->
      <div class="ss-salon-dashboard-view-header">
        <div class="ss-salon-dashboard-view-header__content">
          <h1 class="ss-salon-dashboard-view-header__title">
            {{ $route.meta.title }}
          </h1>

          <!-- Help Popover -->
          <oct-popover
            :anchorOrigin="{
              vertical: 'top',
              horizontal: 'right',
            }"
          > 
            <oct-icon
              icon="help"
              class="ss-dashboard-header__icon"
            />

            <template #popper>
              <div class="ss-dashboard-header-help">
                <div v-if="$route.name === 'SalonDashboard'">
                  <p class="ss-dashboard-header-help__text ss-dashboard-header-help__text--bulleted">
                    スタイル送信数：お客様に送信したスタイルの総数
                  </p>
                  <p class="ss-dashboard-header-help__text ss-dashboard-header-help__text--bulleted">
                    ユニーク顧客数：スタイル送信したお客様の総数（該当期間にて1人のお客様に3回送信していたとしても1とカウント）
                  </p>
                  <p class="ss-dashboard-header-help__text ss-dashboard-header-help__text--bulleted">
                    スタイル閲覧数：お客様に送信したスタイルの閲覧の総数
                  </p>
                  <p class="ss-dashboard-header-help__text ss-dashboard-header-help__text--bulleted">
                    カート追加数：スタイル閲覧したお客様がその後milbon:iD内のいづれかの商品をカートに追加した総数（スタイル閲覧後30日間は有効）
                    購入件数：カート追加された商品がmilbon:iD内で購入された回数の総数（スタイル閲覧後30日間は有効）
                  </p>
                </div>

                <p
                  v-if="$route.name === 'SalonCustomerList'"
                  class="ss-dashboard-header-help__text"
                >
                  お客様個別に送信、閲覧、♥、カート、購入数の総数が確認できます。また「氏名」左横にある「＋」をクリックすると、お客様に個々のスタイル毎に閲覧、♥、カート、購入の状態を確認できます。
                </p>
              </div>
            </template>
          </oct-popover>
        </div>

        <SSDashboardDateRangePicker
          @change="$_ssSDV_onChangeDateRange"
        />
      </div>

      <router-view
        v-if="$data.$_ssSDV_startDate && $data.$_ssSDV_endDate"
        :salonId="salonId"
        :startDate="$data.$_ssSDV_startDate"
        :endDate="$data.$_ssSDV_endDate"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import API from '@/api/index.js';
import OctPrototypeView from '@/mixins/OctPrototypeView.js';
import OctIcon from '@/components/icon/OctIcon.vue'
import OctMedia from '@/components/media/OctMedia.vue'
import OctSwitch from '@/components/switch/OctSwitch.vue'
import OctTabs from '@/components/tabs/OctTabs.vue'
import OctPopover from '@/components/popover/OctPopover.vue'
import SSDashboardDateRangePicker from '@/components/dashboard-date-range-picker/SSDashboardDateRangePicker.vue'

export default {
  name: 'SSSalonDashboardView',
  mixins: [
    OctPrototypeView
  ],
  components: {
    OctIcon,
    OctMedia,
    OctSwitch,
    OctTabs,
    OctPopover,
    SSDashboardDateRangePicker,
  },
  data: () => {
    return {
      /**
       * Start date of the date range.
       * @type {string}
       */
      $_ssSDV_startDate: null,
      /**
       * End date of the date range.
       * @type {string}
       */
      $_ssSDV_endDate: null,
      /**
       * Tabs.
       * @type {Array<{text: string, selected: boolean}>}
       */
      $_ssSDV_tabs: [
        {
          text: 'ダッシュボード',
          selected: false,
        },
        {
          text: '顧客一覧',
          selected: false,
        },
      ],
      /**
       * Switch value.
       * @type {boolean}
       */
      $_ssGN_switchValue: false,
    }
  },
  computed: {
    /** Salon ID for the current environment */
    salonId: vm => /^(stg\.|localhost)/.test(location.hostname) ? '17709784' : vm.$route.params.salonId,
    isNotificationRead: vm =>
      !vm.$store.state.staffs.me.notifications.all.length
      || (
        vm.$store.state.staffs.me.data.lastNotificationReadDate &&
        vm.$store.state.staffs.me.data.lastNotificationReadDate.getTime() >=
          vm.$store.state.staffs.me.notifications.all[0].publishedAt.getTime()
      ),
    /** Unread notifications and news */
    unreadNotifications: vm => [...vm.$store.getters['staffs/me/notifications/unread'], ...vm.$store.getters['news/unread']],
    items: vm => vm.$store.getters['treatments/collection']('mine')['items'],
    unsent: vm => vm.$store.getters['treatments/collection']('mine')['items'].filter(item => item.status < 4),
    sent: vm => vm.$store.getters['treatments/collection']('mine')['items'].filter(item => item.status === 4),
    ...mapState({
      me: state => state.staffs.me,
    }),
    newsBanner: () => null,
    /** Whether to show the switcher */
    $_ssGN_showSwitcher: vm => vm.$store.getters['staffs/me/isOwner'] && vm.$store.getters['hasBetaAccess']
  },
  methods: {
    /**
     * Date range change handler.
     * Updates the start and end dates.
     * @param {Object} dateRange - The date range.
     * @param {string} dateRange.startDate - The start date.
     * @param {string} dateRange.endDate - The end date.
     */
    $_ssSDV_onChangeDateRange(dateRange) {
      this.$data.$_ssSDV_startDate = dateRange.startDate;
      this.$data.$_ssSDV_endDate = dateRange.endDate;
    },
    /**
     * Tab click handler.
     * Updates the selected tab and navigates to the corresponding route.
     * @param {number} index - The index of the tab.
     */
    onClickTab(index) {
      this.$data.$_ssSDV_tabs.forEach(tab => {
        tab.selected = false;
      });
      this.$data.$_ssSDV_tabs[index].selected = true;

      this.$router.push({
        name: index === 0 ? 'SalonDashboard' : 'SalonCustomerList',
        params: {
          salonId: this.salonId
        }
      })
    },
    /** Sign out */
    signOut () {
      API.signOut()
        .then(() => {
          this.$router.replace({name:'Welcome'}).catch(() => {})
        })
        .catch(thrown => this.$store.commit('error', thrown))
    },
  },
  mounted() {
    if (this.$route.name === 'SalonDashboard') {
      this.$data.$_ssSDV_tabs[0].selected = true;
    } else {
      this.$data.$_ssSDV_tabs[1].selected = true;
    }
  },
  watch: {
    /**
     * Navigate between the owner dashboard and treatment management when the application switcher changes.
     * @param {boolean} val - `true` for treatment management (`Dashboard`); `false` for the owner dashboard (`SalonDashboard`).
     */
    '$data.$_ssGN_switchValue': {
      handler(val) {
        val
        ? this.$router.push({name: 'Dashboard'})
        : this.$router.push({name: 'SalonDashboard', params: {salonId: this.salonId}})
      },
    },
  },
}
</script>

<style scoped lang="scss">
@import "../components/theme/variables";
@import "../components/grid/variables";
@import "../components/typography/variables";
@import "../components/typography/mixins";

.ss-salon-dashboard-view {
  margin: 0 auto;
  max-width: oct-rem(1920);
  min-width: oct-rem(768);
}

.ss-salon-dashboard-view__tabs {
  padding: 0 oct-rem(32);
  border-bottom: oct-rem(1) solid $oct-theme--neutral-20;
}

.ss-salon-dashboard-view__content {
  display: flex;
  flex-direction: column;
  gap: oct-rem(64);
  padding: oct-rem(64);
}

.ss-salon-dashboard-view-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.ss-salon-dashboard-view-header__content {
  display: flex;
  align-items: center;
  gap: oct-rem(8);
}

.ss-salon-dashboard-view-header__title {
  font-size: oct-rem(24);
  font-weight: 600;
  line-height: 1;
  margin: 0;
}

.ss-dashboard-header__icon {
  display: block;
  width: oct-rem(16);
  height: oct-rem(16);
}

.ss-dashboard-header-help {
  margin-left: oct-rem(8);
  width: oct-rem(304);
  padding: oct-rem(16);
  background-color: $oct-theme--surface;
  border: oct-rem(1) solid $oct-theme--neutral-20;
}

.ss-dashboard-header-help__text {
  font-size: oct-rem(12);
  line-height: 1.5;
  margin: 0;

  &--bulleted {
    margin-left: oct-rem(18);

    &:before {
      content: "・";
      margin-left: oct-rem(-18);
    }
  }
}

.ss-dashboard-header {
  position: sticky;
  top: 0;
  margin-bottom: oct-rem(32);
  background-color: $oct-theme--surface;
  z-index: 2;

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  margin-bottom: oct-rem(64);
}
}

.ss-dashboard-header__content {
  position: relative;
  display: flex;
  align-items: center;
  height: oct-rem(80);
  padding: oct-rem(16);

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  height: oct-rem(120);
  padding: oct-rem(32);

  &--with-switcher {
    display: grid;
    grid-template-columns: oct-rem(180) 1fr oct-rem(180);

    .ss-dashboard-header__title {
      display: none;
      text-align: left;
    }

    .ss-dashboard-header__graphic {
      margin-right: 0;
    }
  }
}

@media screen and (min-width: oct-rem(1280)) {
  &--with-switcher {
    grid-template-columns: auto oct-rem(200) 1fr oct-rem(288);
    gap: oct-rem(32);

    .ss-dashboard-header__title {
      display: block;
    }
  }
}
}

.ss-dashboard-header__switcher {
  display: none;

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  display: block;
  justify-self: center;

  ::v-deep .oct-switch__primary-text {
    width: oct-rem(160);
  }

  ::v-deep .oct-switch__native-control {
    cursor: pointer;
  }
}

@media screen and (min-width: oct-rem(1280)) {
  ::v-deep .oct-switch__primary-text {
    width: oct-rem(204);
  }
}
}

.ss-dashboard-header__graphic {
  margin-right: oct-rem(76-48);
  flex: 0 0 oct-rem(48);
  max-width: oct-rem(48);
  width: oct-rem(48);
  height: oct-rem(48);
  border: 1px solid $oct-theme--neutral-20;

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  margin-right: oct-rem(144-56);
  flex: 0 0 oct-rem(56);
  max-width: oct-rem(56);
  width: oct-rem(56);
  height: oct-rem(56);
}
}

.ss-dashboard-header__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ss-dashboard-header__title {
  flex: 1 1 auto;
  text-align: center;

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  font-size: oct-rem(20);
}
}

.ss-dashboard-header__actions {
  position: relative;
  flex: 0 0 auto;
  gap: oct-rem(12);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  background-color: $oct-theme--surface;
  z-index: 2;

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  gap: oct-rem(24);
}
}

.ss-dashboard-header__action {
  width: oct-rem(32);
  height: oct-rem(32);
  cursor: pointer;

  &--exit {
    display: none;
  }

  .oct-icon {
    fill: $oct-theme--neutral;
    width: 100%;
    height: 100%;
  }

  &--activated {
    position: relative;

    .oct-icon {
      fill: $oct-theme--primary;
    }

    &:after {
      content: "";
      position: absolute;
      top: oct-rem(-2);
      right: oct-rem(-2);
      width: oct-rem(12);
      height: oct-rem(12);
      background-color: $oct-theme--neutral-30;
      border: oct-rem(2) solid $oct-theme--surface;
      border-radius: 50%;
    }
  }

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  &--exit {
    display: block;
  }
}
}
</style>